import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './LogoIsbasiModal.styled';

const LogoIsbasiModal = NiceModal.create(({ logo, title }) => {
	const modal = useModal();
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const user = useSelector((state) => state.user);
	const { ID: userId, companyId } = user;

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					firstName: yup.string().nullable().required(),
					lastName: yup.string().nullable().required(),
					phoneNumber: yup.string().nullable().phoneNumber().required(),
					email: yup.string().nullable().email().required(),
					companyName: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const handleCloseModal = () => {
		modal.remove();
	};

	const handleConnect = async (data) => {
		try {
			const response = await axiosPrivate.post(OPEN.LOGO_INTEGRATION_SIGNUP, {
				...data,
				userId,
				companyId
			});

			if (response.status === 200) {
				NiceModal.show(ConfirmationModal, {
					lottieName: 'success',
					title: 'logoisbasi.integrationSuccessHeader',
					subTitle: 'logoisbasi.integrationSuccessMessage',
					yesAction: () => {
						window.open(t('logoisbasi.appLink'), '_blank').focus();
					},
					confirmLabel: 'logoisbasi.goToApp',
					rejectLabel: 'close'
				});
			}
		} catch (error) {
			showError({ message: error?.response?.data?.code });
		} finally {
			handleCloseModal();
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			firstName: null,
			lastName: null,
			phoneNumber: null,
			email: null,
			companyName: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	return (
		<Modal fullwidth maxWidth="medium" className="modal-logoIsbasiIntegration">
			<ModalTitle>
				<S.TitleContainer>
					<S.Logo src={logo} alt="logoIsbasi" width="6rem" height="6rem" />
					<Typography variant="p1" fontWeight="500">
						{title}
					</Typography>
				</S.TitleContainer>
			</ModalTitle>
			<ModalContent>
				<S.ContentContainer>
					<Form formMethods={formMethods} borderless>
						<FormItem colSpan={{ md: 6 }} label="firstName" isLoading={isLoading} required>
							<StringInput name="firstName" />
						</FormItem>

						<FormItem colSpan={{ md: 6 }} label="lastName" isLoading={isLoading} required>
							<StringInput name="lastName" />
						</FormItem>

						<FormItem colSpan={{ md: 6 }} label="phoneNumber" isLoading={isLoading} required>
							<PhoneNumber
								onChange={(value) => {
									setValue('phoneNumber', value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch('phoneNumber')}
								name="phone"
								errorMessage={errors?.phoneNumber?.message}
							/>
						</FormItem>

						<FormItem colSpan={{ md: 6 }} label="email" isLoading={isLoading} required>
							<StringInput name="email" />
						</FormItem>

						<FormItem colSpan={{ md: 6 }} label="companyName" isLoading={isLoading} required>
							<StringInput name="companyName" />
						</FormItem>
					</Form>

					<InstructionComponent name="logoisbasi" />
				</S.ContentContainer>
			</ModalContent>
			<ModalActions>
				<Button onClick={handleCloseModal} loading={isLoading} label="cancel" variant="borderless" />
				<Button
					loading={isLoading}
					onClick={handleSubmit(handleConnect, (errors) => {
						AppLogger.log(errors);
					})}
					label="connect"
				/>
			</ModalActions>
		</Modal>
	);
});

export default LogoIsbasiModal;

const Ul = ({ children }) => <ul>{children}</ul>;
const Li = ({ children }) => <li>{children}</li>;
const LinkViewer = ({ children }) => {
	const { t } = useTranslation();
	return (
		<a href={children} target="_blank" rel="noreferrer">
			{t(`logoisbasi.${children}`)}
		</a>
	);
};

const InstructionComponent = ({ name }) => {
	const { t } = useTranslation();
	const AccordionArr = useMemo(() => {
		const arr = [];
		for (let i = 1; i < 30; i += 1) {
			if (t(`${name}.instructionsStep${i}`) !== `${name}.instructionsStep${i}`) {
				arr.push(i);
			} else {
				break;
			}
		}
		return arr;
	}, [name, t]);

	return AccordionArr.map((i) => (
		<Accordion key={i} header={`${name}.instructionsTitle${i}`} defaultVisible={i === 1}>
			<Typography noWrap>
				<Trans
					i18nKey={`${name}.instructionsStep${i}`}
					components={{
						linkViewer: <LinkViewer />,
						ul: <Ul />,
						li: <Li />
					}}
				/>
			</Typography>
		</Accordion>
	));
};
